import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Filter, Loader2 } from "lucide-react";
import { powerList, POWER_CATEGORIES, powerBookmarkToggle } from "@/lib/powerApi";
import PetitionCard from "@/components/power/PetitionCard";
import PowerShareDialog from "@/components/power/PowerShareDialog";

const SORTS = [
  { id: "trending",     label: "Trending" },
  { id: "newest",       label: "Newest" },
  { id: "ending",       label: "Ending soon" },
  { id: "most_signed",  label: "Most signed" },
  { id: "featured",     label: "Featured" },
  { id: "close_to_goal",label: "Almost there" },
  { id: "completed",    label: "Victories" },
];

export default function PowerExplore() {
  const loc = useLocation();
  const nav = useNavigate();
  const params = new URLSearchParams(loc.search);

  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "All");
  const [sort, setSort] = useState(params.get("sort") || "trending");
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [bookmarks, setBookmarks] = useState({});
  const sentinel = useRef(null);
  const reqId = useRef(0);
  const initialLoadDone = useRef(false);

  const fetchPage = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    const myReq = ++reqId.current;
    try {
      const nextOff = reset ? 0 : offset;
      const r = await powerList({ q: q || undefined, category: category === "All" ? undefined : category, sort, limit: 12, offset: nextOff });
      // Drop stale responses from prior filter/search
      if (myReq !== reqId.current) return;
      setItems((prev) => reset ? r.petitions : [...prev, ...r.petitions]);
      if (r.next_offset === null || r.next_offset === undefined) { setDone(true); setNextOffset(null); }
      else { setNextOffset(r.next_offset); setDone(false); }
      setOffset(r.next_offset ?? nextOff);
      if (reset) initialLoadDone.current = true;
    } catch {
      if (myReq === reqId.current) toast.error("Couldn't load petitions");
    } finally { if (myReq === reqId.current) setLoading(false); }
  // eslint-disable-next-line
  }, [q, category, sort, offset]);

  // Reset when filters change (also mirror to URL so back button works)
  useEffect(() => {
    initialLoadDone.current = false;
    setItems([]); setOffset(0); setDone(false); setNextOffset(0);
    const usp = new URLSearchParams();
    if (q) usp.set("q", q);
    if (category !== "All") usp.set("category", category);
    if (sort !== "trending") usp.set("sort", sort);
    nav({ pathname: "/power/explore", search: usp.toString() ? `?${usp.toString()}` : "" }, { replace: true });
    fetchPage(true);
  // eslint-disable-next-line
  }, [q, category, sort]);

  // Infinite scroll via IntersectionObserver — only after the initial page has landed
  useEffect(() => {
    if (!sentinel.current || done) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !done && nextOffset !== null && nextOffset !== 0 && initialLoadDone.current) {
        fetchPage(false);
      }
    }, { rootMargin: "600px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [fetchPage, loading, done, nextOffset]);

  const toggleBookmark = async (p) => {
    try {
      const r = await powerBookmarkToggle(p.slug);
      setBookmarks((b) => ({ ...b, [p.slug]: r.bookmarked }));
      toast.success(r.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sign in to bookmark");
      else toast.error("Couldn't bookmark");
    }
  };

  return (
    <div data-testid="power-explore" className="space-y-4 pb-6">
      <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-[#F6F8FC]/95 backdrop-blur">
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1A2F]/40" />
          <input data-testid="explore-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, keyword, creator, location…"
            className="w-full pl-9 pr-3 py-3 rounded-full bg-white border border-[#E4EAF3] text-sm focus:outline-none focus:border-[#1E88FF]" />
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar">
          {SORTS.map((s) => (
            <button key={s.id} data-testid={`sort-${s.id}`} onClick={() => setSort(s.id)}
              className={"whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-colors " + (sort === s.id ? "bg-[#1E88FF] text-white" : "bg-white border border-[#E4EAF3] text-[#0A1A2F]/70 hover:border-[#1E88FF]")}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-2">
          {["All", ...POWER_CATEGORIES].map((c) => (
            <button key={c} data-testid={`cat-${c}`} onClick={() => setCategory(c)}
              className={"whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-medium transition-colors " + (category === c ? "bg-[#0A1A2F] text-white" : "bg-white border border-[#E4EAF3] text-[#0A1A2F]/70 hover:border-[#0A1A2F]")}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 && !loading && (
        <div data-testid="explore-empty" className="text-center py-12 text-[#0A1A2F]/60">
          <Filter className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No petitions match. Try broader filters.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((p) => (
          <PetitionCard key={p.slug} petition={p} onShare={setSharing} onBookmark={toggleBookmark} bookmarked={!!bookmarks[p.slug]} />
        ))}
      </div>

      <div ref={sentinel} className="flex items-center justify-center py-6">
        {loading && <div className="inline-flex items-center gap-2 text-[#0A1A2F]/50 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading more…</div>}
        {!loading && done && items.length > 0 && <div className="text-[#0A1A2F]/40 text-xs">You've reached the end.</div>}
      </div>

      {sharing && <PowerShareDialog url={`${window.location.origin}/power/p/${sharing.slug}`} title={sharing.title} onClose={() => setSharing(null)} />}
    </div>
  );
}
